import { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Upload, Input, Select, Modal, Table, Tag, Space,
  message, Popconfirm, Typography, Row, Col, Image, Tooltip, Empty,
} from 'antd';
import {
  Upload as UploadIcon, Trash2, Copy, Edit2, Search,
  Image as ImageIcon, Video, FileText, FolderOpen,
} from 'lucide-react';
import { assetsApi } from '../../services/api';

const { Title, Text } = Typography;
const { Dragger } = Upload;

interface SiteAsset {
  id: string;
  key: string;
  filename: string;
  originalName: string;
  url: string;
  mimeType: string;
  size: number;
  category: string;
  alt: string | null;
  createdAt: string;
  updatedAt: string;
}

const CATEGORIES = [
  { value: 'LOGO', label: 'Logos', color: 'gold', icon: <ImageIcon size={14} /> },
  { value: 'IMAGE', label: 'Imágenes', color: 'blue', icon: <ImageIcon size={14} /> },
  { value: 'VIDEO', label: 'Vídeos', color: 'purple', icon: <Video size={14} /> },
  { value: 'ICON', label: 'Iconos', color: 'green', icon: <FileText size={14} /> },
  { value: 'DOCUMENT', label: 'Documentos', color: 'default', icon: <FileText size={14} /> },
];

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function AssetsPage() {
  const [assets, setAssets] = useState<SiteAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<SiteAsset | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadKey, setUploadKey] = useState('');
  const [uploadCategory, setUploadCategory] = useState('IMAGE');
  const [uploadAlt, setUploadAlt] = useState('');
  const [uploading, setUploading] = useState(false);

  // Edit form state
  const [editKey, setEditKey] = useState('');
  const [editAlt, setEditAlt] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await assetsApi.list(filter);
      setAssets(data);
    } catch (err) {
      message.error('Error cargando assets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadKey) {
      message.warning('Archivo y clave son obligatorios');
      return;
    }
    setUploading(true);
    try {
      await assetsApi.upload(uploadFile, uploadKey, uploadCategory, uploadAlt || undefined);
      message.success('Asset subido correctamente');
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadKey('');
      setUploadCategory('IMAGE');
      setUploadAlt('');
      fetchAssets();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Error subiendo asset');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await assetsApi.delete(id);
      message.success('Asset eliminado');
      fetchAssets();
    } catch {
      message.error('Error eliminando asset');
    }
  };

  const handleEdit = (asset: SiteAsset) => {
    setEditingAsset(asset);
    setEditKey(asset.key);
    setEditAlt(asset.alt || '');
    setEditCategory(asset.category);
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingAsset) return;
    try {
      await assetsApi.update(editingAsset.id, {
        key: editKey,
        alt: editAlt,
        category: editCategory,
      });
      message.success('Asset actualizado');
      setEditModalOpen(false);
      fetchAssets();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Error actualizando');
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success('URL copiada al portapapeles');
  };

  const isImage = (mime: string) => mime.startsWith('image/');
  const isVideo = (mime: string) => mime.startsWith('video/');

  const filtered = assets.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      a.key.toLowerCase().includes(s) ||
      a.originalName.toLowerCase().includes(s) ||
      (a.alt || '').toLowerCase().includes(s)
    );
  });

  const catConfig = (cat: string) =>
    CATEGORIES.find((c) => c.value === cat) || CATEGORIES[1];

  // ── Table columns ──
  const columns = [
    {
      title: 'Preview',
      key: 'preview',
      width: 80,
      render: (_: any, r: SiteAsset) =>
        isImage(r.mimeType) ? (
          <Image src={r.url} width={60} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
        ) : isVideo(r.mimeType) ? (
          <div style={{ width: 60, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Video size={20} color="#999" />
          </div>
        ) : (
          <div style={{ width: 60, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={20} color="#999" />
          </div>
        ),
    },
    {
      title: 'Clave',
      dataIndex: 'key',
      key: 'key',
      render: (key: string) => <Text code>{key}</Text>,
    },
    {
      title: 'Nombre original',
      dataIndex: 'originalName',
      key: 'originalName',
      ellipsis: true,
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      render: (cat: string) => {
        const c = catConfig(cat);
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'Tamaño',
      dataIndex: 'size',
      key: 'size',
      render: (size: number) => formatBytes(size),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, r: SiteAsset) => (
        <Space>
          <Tooltip title="Copiar URL">
            <Button type="text" size="small" icon={<Copy size={14} />} onClick={() => copyUrl(r.url)} />
          </Tooltip>
          <Tooltip title="Editar">
            <Button type="text" size="small" icon={<Edit2 size={14} />} onClick={() => handleEdit(r)} />
          </Tooltip>
          <Popconfirm title="¿Eliminar este asset?" onConfirm={() => handleDelete(r.id)}>
            <Button type="text" size="small" danger icon={<Trash2 size={14} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Assets del sitio</Title>
          <Text type="secondary">
            Gestiona logos, imágenes, vídeos e iconos del sitio web
          </Text>
        </div>
        <Button
          type="primary"
          icon={<UploadIcon size={16} />}
          onClick={() => setUploadModalOpen(true)}
          style={{ background: '#cfb56b', borderColor: '#cfb56b' }}
        >
          Subir asset
        </Button>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Buscar por clave, nombre o alt..."
              prefix={<Search size={16} color="#999" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="Categoría"
              value={filter}
              onChange={setFilter}
              allowClear
              style={{ width: 160 }}
              options={[
                { value: undefined, label: 'Todas' },
                ...CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
              ]}
            />
          </Col>
          <Col>
            <Button.Group>
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                onClick={() => setViewMode('grid')}
                style={viewMode === 'grid' ? { background: '#1F2F3F', borderColor: '#1F2F3F' } : {}}
              >
                Grid
              </Button>
              <Button
                type={viewMode === 'table' ? 'primary' : 'default'}
                onClick={() => setViewMode('table')}
                style={viewMode === 'table' ? { background: '#1F2F3F', borderColor: '#1F2F3F' } : {}}
              >
                Tabla
              </Button>
            </Button.Group>
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {CATEGORIES.map((c) => {
          const count = assets.filter((a) => a.category === c.value).length;
          return (
            <Col key={c.value} xs={12} sm={8} md={4}>
              <Card
                size="small"
                hoverable
                onClick={() => setFilter(filter === c.value ? undefined : c.value)}
                style={{
                  cursor: 'pointer',
                  borderColor: filter === c.value ? '#cfb56b' : undefined,
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <Text strong style={{ fontSize: 20 }}>{count}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>{c.label}</Text>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Grid view */}
      {viewMode === 'grid' ? (
        filtered.length === 0 ? (
          <Card>
            <Empty description="No hay assets" />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {filtered.map((asset) => (
              <Col key={asset.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={
                    isImage(asset.mimeType) ? (
                      <div style={{ height: 160, overflow: 'hidden', background: '#fafafa' }}>
                        <img
                          src={asset.url}
                          alt={asset.alt || asset.key}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    ) : isVideo(asset.mimeType) ? (
                      <div
                        style={{
                          height: 160, background: '#f0f0f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Video size={40} color="#999" />
                      </div>
                    ) : (
                      <div
                        style={{
                          height: 160, background: '#f0f0f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <FolderOpen size={40} color="#999" />
                      </div>
                    )
                  }
                  actions={[
                    <Tooltip title="Copiar URL" key="copy">
                      <Copy size={16} onClick={() => copyUrl(asset.url)} style={{ cursor: 'pointer' }} />
                    </Tooltip>,
                    <Tooltip title="Editar" key="edit">
                      <Edit2 size={16} onClick={() => handleEdit(asset)} style={{ cursor: 'pointer' }} />
                    </Tooltip>,
                    <Popconfirm title="¿Eliminar?" onConfirm={() => handleDelete(asset.id)} key="del">
                      <Trash2 size={16} color="#ff4d4f" style={{ cursor: 'pointer' }} />
                    </Popconfirm>,
                  ]}
                >
                  <Card.Meta
                    title={
                      <Tooltip title={asset.key}>
                        <Text code style={{ fontSize: 13 }}>{asset.key}</Text>
                      </Tooltip>
                    }
                    description={
                      <div>
                        <Tag color={catConfig(asset.category).color} style={{ marginBottom: 4 }}>
                          {catConfig(asset.category).label}
                        </Tag>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {asset.originalName}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatBytes(asset.size)}
                        </Text>
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )
      ) : (
        <Card>
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            size="middle"
          />
        </Card>
      )}

      {/* Upload Modal */}
      <Modal
        title="Subir nuevo asset"
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false);
          setUploadFile(null);
          setUploadKey('');
          setUploadAlt('');
        }}
        onOk={handleUpload}
        confirmLoading={uploading}
        okText="Subir"
        okButtonProps={{ style: { background: '#cfb56b', borderColor: '#cfb56b' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          <Dragger
            maxCount={1}
            beforeUpload={(file) => {
              setUploadFile(file);
              // Auto-generate key from filename
              if (!uploadKey) {
                const name = file.name
                  .replace(/\.[^/.]+$/, '')
                  .replace(/[^a-zA-Z0-9_-]/g, '-')
                  .toLowerCase();
                setUploadKey(name);
              }
              // Auto-detect category
              if (file.type.startsWith('image/')) {
                if (file.name.toLowerCase().includes('logo')) setUploadCategory('LOGO');
                else if (file.type === 'image/svg+xml') setUploadCategory('ICON');
                else setUploadCategory('IMAGE');
              } else if (file.type.startsWith('video/')) {
                setUploadCategory('VIDEO');
              } else {
                setUploadCategory('DOCUMENT');
              }
              return false; // Prevent auto upload
            }}
            accept="image/*,video/*,.svg,.pdf,.doc,.docx"
          >
            <p style={{ marginBottom: 8 }}>
              <UploadIcon size={32} color="#cfb56b" />
            </p>
            <p>Arrastra un archivo aquí o haz clic para seleccionar</p>
            <p style={{ color: '#999', fontSize: 12 }}>
              Imágenes, vídeos, SVGs, documentos (máx. 100MB)
            </p>
          </Dragger>

          {uploadFile && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              Archivo: {uploadFile.name} ({formatBytes(uploadFile.size)})
            </Text>
          )}

          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Clave única <Text type="danger">*</Text>
            </Text>
            <Input
              placeholder="ej: logo, hero-video-webm, about-image-1"
              value={uploadKey}
              onChange={(e) => setUploadKey(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase())}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Identificador único para referenciar este asset en el código
            </Text>
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Categoría</Text>
            <Select
              value={uploadCategory}
              onChange={setUploadCategory}
              style={{ width: '100%' }}
              options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
            />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Texto alternativo (alt)</Text>
            <Input
              placeholder="Descripción de la imagen para SEO y accesibilidad"
              value={uploadAlt}
              onChange={(e) => setUploadAlt(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Editar asset"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleEditSave}
        okText="Guardar"
        okButtonProps={{ style: { background: '#cfb56b', borderColor: '#cfb56b' } }}
      >
        {editingAsset && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
            {isImage(editingAsset.mimeType) && (
              <div style={{ textAlign: 'center', padding: 16, background: '#fafafa', borderRadius: 8 }}>
                <img
                  src={editingAsset.url}
                  alt={editingAsset.alt || editingAsset.key}
                  style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain' }}
                />
              </div>
            )}

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>URL</Text>
              <Input value={editingAsset.url} readOnly addonAfter={
                <Copy size={14} style={{ cursor: 'pointer' }} onClick={() => copyUrl(editingAsset.url)} />
              } />
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Clave</Text>
              <Input
                value={editKey}
                onChange={(e) => setEditKey(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase())}
              />
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Categoría</Text>
              <Select
                value={editCategory}
                onChange={setEditCategory}
                style={{ width: '100%' }}
                options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
              />
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Texto alternativo</Text>
              <Input
                value={editAlt}
                onChange={(e) => setEditAlt(e.target.value)}
                placeholder="Descripción de la imagen"
              />
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Archivo: {editingAsset.originalName} · {formatBytes(editingAsset.size)} · {editingAsset.mimeType}
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
